import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// Import tab components
import PersonalInformation from './tabs/PersonalInformation';
import OperationalInformation from './tabs/OperationalInformation';
import LegalInfo from './tabs/LegalInfo';
import SocialMediaLinks from './tabs/SocialMediaLinks';
import OnboardingTemplates from './tabs/OnboardingTemplates';

import { adminMenuItems } from '../../../../config/layout';
import { useGetCompaniesQuery, useUpdateCompanyMutation, useCreateCompanyMutation } from '../../../../services/orgApi';
import AlertDialog from '../../../../components/common/AlertDialog';
import useAlertDialog from '../../../../hooks/useAlertDialog';

/**
 * OrgSetup Component
 * Main component for organization setup with tab-based navigation
 * Follows the same structure as OrgStructure component
 */
const OrgSetup = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [tabs, setTabs] = useState([]);
  const fileInputRef = useRef(null);
  const { alertDialog, showAlert, closeAlert } = useAlertDialog();

  const { data: companies } = useGetCompaniesQuery();
  const [updateCompany, { isLoading: isUpdating }] = useUpdateCompanyMutation();
  const [createCompany, { isLoading: isCreating }] = useCreateCompanyMutation();

  // Get tabs from layout configuration
  const orgManagementItem = adminMenuItems.find((item) => item.id === 'org-management');

  const currentnavItem = useMemo(() => {
    return orgManagementItem?.subItems?.find((item) => item.id === 'org-setup');
  }, [orgManagementItem]);

  useEffect(() => {
    if (currentnavItem?.tabs) {
      setTabs(currentnavItem.tabs);
    }
  }, [currentnavItem]);

  useEffect(() => {
    if (!tab) {
      setActiveTab(0);
      return;
    }

    const nextIndex = tabs.findIndex((t) => t.value === tab);
    if (nextIndex !== -1) {
      setActiveTab(nextIndex);
    }
  }, [tab, tabs]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(`/org-management/org-setup/${tabs[newValue].value}`);
  };

  // Get breadcrumbs - use active tab's breadcrumbs if available, otherwise use base breadcrumbs
  const currentBreadcrumbs = useMemo(() => {
    if (activeTab >= 0 && tabs[activeTab]?.breadcrumbs) {
      return tabs[activeTab].breadcrumbs;
    }
    return currentnavItem?.breadcrumbs || [];
  }, [activeTab, tabs, currentnavItem]);

  const [formData, setFormData] = useState({
    // Personal Information
    companyName: '',
    legalEntityName: '',
    companyLogo: null,
    address: '',
    country: '',
    city: '',
    state: '',
    postalCode: '',
    phoneNumber: '',
    emailAddress: '',
    websiteUrl: '',
    taxIdentificationNumber: '',
    industryType: '',

    // Operational Information
    employeeCount: '',
    revenue: '',
    foundedYear: '',
    businessHours: '',
    timeZone: '',
    primaryLanguage: '',
    currency: '',

    // Legal Info
    registrationNumber: '',
    taxId: '',
    panNumber: '',
    gstNumber: '',
    cinNumber: '',
    incorporationDate: '',
    legalStructure: '',

    // Social Media Links
    linkedin: '',
    twitter: '',
    facebook: '',
    instagram: '',
    youtube: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form with existing data
  useEffect(() => {
    if (companies && companies.length > 0) {
      const company = companies[0];
      const op = company.operationalInfo || {};
      const legal = company.legalInfo || {};
      const social = company.socialMedia || {};

      setFormData({
        id: company.id,
        companyName: company.name || '',
        legalEntityName: company.legalEntityName || '',
        companyLogo: company.logo || null,
        address: company.address || '',
        country: company.country || '',
        city: company.city || '',
        state: company.state || '',
        postalCode: company.postalCode || '',
        phoneNumber: company.phone || '',
        emailAddress: company.email || '',
        websiteUrl: company.website || '',
        taxIdentificationNumber: company.taxId || '',
        industryType: company.industryType || '',

        employeeCount: op.employeeCount || '',
        revenue: op.revenue || '',
        foundedYear: op.foundedYear || '',
        businessHours: op.businessHours || '',
        timeZone: op.timeZone || '',
        primaryLanguage: op.primaryLanguage || '',
        currency: op.currency || '',

        registrationNumber: legal.registrationNumber || '',
        taxId: legal.taxId || '',
        panNumber: legal.panNumber || '',
        gstNumber: legal.gstNumber || '',
        cinNumber: legal.cinNumber || '',
        incorporationDate: legal.incorporationDate || '',
        legalStructure: legal.legalStructure || '',

        linkedin: social.linkedin || '',
        twitter: social.twitter || '',
        facebook: social.facebook || '',
        instagram: social.instagram || '',
        youtube: social.youtube || ''
      });
    }
  }, [companies]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  }, [errors]);

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size and type
      const maxSize = 4 * 1024 * 1024; // 4MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          companyLogo: 'File size must be less than 5MB'
        }));
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          companyLogo: 'Only PNG, JPG, and GIF formats are allowed'
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        companyLogo: file
      }));

      // Clear any existing logo error
      setErrors(prev => ({
        ...prev,
        companyLogo: null
      }));
    }
  }, []);

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      companyLogo: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // GST validation function
  const validateGSTIN = (gstin) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
    return gstRegex.test(gstin);
  };

  // TIN validation function
  const validateTIN = (tin) => {
    const tinRegex = /^[0-9]{11}$/;
    return tinRegex.test(tin);
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required field validations
    if (!formData.companyName || formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name is required and must be at least 2 characters';
    }

    if (!formData.legalEntityName || formData.legalEntityName.trim().length < 2) {
      newErrors.legalEntityName = 'Legal entity name is required and must be at least 2 characters';
    }

    // Email validation
    if (formData.emailAddress && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    // Phone validation
    if (formData.phoneNumber && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phoneNumber.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Website validation
    if (formData.websiteUrl && !/^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/.test(formData.websiteUrl)) {
      newErrors.websiteUrl = 'Please enter a valid website URL';
    }

    // Employee count validation
    if (formData.employeeCount && (isNaN(formData.employeeCount) || parseInt(formData.employeeCount) < 1)) {
      newErrors.employeeCount = 'Employee count must be a positive number';
    }

    // Founded year validation
    if (formData.foundedYear) {
      const year = parseInt(formData.foundedYear);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1600 || year > currentYear) {
        newErrors.foundedYear = `Please enter a valid year between 1600 and ${currentYear}`;
      }
    }

    // Currency validation
    if (formData.currency && !/^[A-Z]{3}$/.test(formData.currency)) {
      newErrors.currency = 'Currency must be a valid 3-letter code (e.g., USD, EUR)';
    }

    // PAN number validation
    if (formData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) {
      newErrors.panNumber = 'PAN must be in format: ABCDE1234F';
    }

    // GST number validation with example
    if (formData.gstNumber && !validateGSTIN(formData.gstNumber)) {
      newErrors.gstNumber = 'GST must be 15 characters. Example: 29AAAPL1234C1ZV (State Code + PAN + Check Code)';
    }

    // TIN validation with example
    if (formData.taxId && !validateTIN(formData.taxId)) {
      newErrors.taxId = 'TIN must be 11 digits. Example: 12345678901';
    }

    // CIN number validation
    if (formData.cinNumber && !/^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(formData.cinNumber)) {
      newErrors.cinNumber = 'CIN must be 21 characters in valid format';
    }

    // Social media URL validations
    const urlValidators = {
      linkedin: /^(https?:\/\/)?(www\.)?linkedin\.com\/.+/,
      twitter: /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/.+/,
      facebook: /^(https?:\/\/)?(www\.)?facebook\.com\/.+/,
      instagram: /^(https?:\/\/)?(www\.)?instagram\.com\/.+/,
      youtube: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/
    };

    Object.keys(urlValidators).forEach(platform => {
      if (formData[platform] && !urlValidators[platform].test(formData[platform])) {
        newErrors[platform] = `Please enter a valid ${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`;
      }
    });

    return newErrors;
  }, [formData]);

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      // Validate form
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        // Mark all fields as touched to show errors
        setTouched(prev => {
          const newTouched = { ...prev };
          Object.keys(validationErrors).forEach(key => {
            newTouched[key] = true;
          });
          return newTouched;
        });
        setIsSubmitting(false);
        showAlert({
          title: 'Validation Errors',
          message: 'Please fix the validation errors before saving.',
          severity: 'warning'
        });
        return;
      }


      const payload = {
        name: formData.companyName,
        legalEntityName: formData.legalEntityName,
        address: formData.address,
        country: formData.country,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        phone: formData.phoneNumber,
        email: formData.emailAddress,
        website: formData.websiteUrl,
        taxId: formData.taxIdentificationNumber,
        industryType: formData.industryType,
        operationalInfo: {
          employeeCount: formData.employeeCount,
          revenue: formData.revenue,
          foundedYear: formData.foundedYear,
          businessHours: formData.businessHours,
          timeZone: formData.timeZone,
          primaryLanguage: formData.primaryLanguage,
          currency: formData.currency,
        },
        legalInfo: {
          registrationNumber: formData.registrationNumber,
          taxId: formData.taxId,
          panNumber: formData.panNumber,
          gstNumber: formData.gstNumber,
          cinNumber: formData.cinNumber,
          incorporationDate: formData.incorporationDate,
          legalStructure: formData.legalStructure,
        },
        socialMedia: {
          linkedin: formData.linkedin,
          twitter: formData.twitter,
          facebook: formData.facebook,
          instagram: formData.instagram,
          youtube: formData.youtube,
        },
      };


      // Check if company exists (update) or create new
      if (formData.id) {
        // Update existing company
        const updatePayload = { ...payload, id: formData.id };
        await updateCompany(updatePayload).unwrap();
      } else {
        // Create new company
        await createCompany(payload).unwrap();
        showAlert({
          title: 'Success',
          message: 'Organization created successfully!',
          severity: 'success'
        });
      }

      setErrors({});
      setTouched({});
    } catch (err) {
      console.error('Failed to save company:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset to last fetched data
    if (companies && companies.length > 0) {
      const company = companies[0];
      // Reuse the same logic as in useEffect
      const op = company.operationalInfo || {};
      const legal = company.legalInfo || {};
      const social = company.socialMedia || {};

      setFormData({
        id: company.id,
        companyName: company.name || '',
        legalEntityName: company.legalEntityName || '',
        companyLogo: company.logo || null,
        address: company.address || '',
        country: company.country || '',
        city: company.city || '',
        state: company.state || '',
        postalCode: company.postalCode || '',
        phoneNumber: company.phone || '',
        emailAddress: company.email || '',
        websiteUrl: company.website || '',
        taxIdentificationNumber: company.taxId || '',
        industryType: company.industryType || '',

        employeeCount: op.employeeCount || '',
        revenue: op.revenue || '',
        foundedYear: op.foundedYear || '',
        businessHours: op.businessHours || '',
        timeZone: op.timeZone || '',
        primaryLanguage: op.primaryLanguage || '',
        currency: op.currency || '',

        registrationNumber: legal.registrationNumber || '',
        taxId: legal.taxId || '',
        panNumber: legal.panNumber || '',
        gstNumber: legal.gstNumber || '',
        cinNumber: legal.cinNumber || '',
        incorporationDate: legal.incorporationDate || '',
        legalStructure: legal.legalStructure || '',

        linkedin: social.linkedin || '',
        twitter: social.twitter || '',
        facebook: social.facebook || '',
        instagram: social.instagram || '',
        youtube: social.youtube || ''
      });

      // Clear validation state
      setErrors({});
      setTouched({});
    }
  };

  return (
    <Container maxWidth="xl" sx={{ m: 0, p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{
          marginBottom: 3,
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.875rem',
          },
        }}
      >
        {currentBreadcrumbs.map((crumb, index) => (
          <Link
            key={crumb.path}
            underline={index === currentBreadcrumbs.length - 1 ? 'none' : 'hover'}
            color={index === currentBreadcrumbs.length - 1 ? 'inherit' : 'inherit'}
            sx={{
              cursor: index === currentBreadcrumbs.length - 1 ? 'default' : 'pointer',
              color: index === currentBreadcrumbs.length - 1 ? '#7522D1' : '#4F4937',
              fontWeight: index === currentBreadcrumbs.length - 1 ? 600 : 400,
              '&:hover': {
                color:
                  index === currentBreadcrumbs.length - 1 ? '#7522D1' : '#7522D1',
              },
            }}
            onClick={() => index < currentBreadcrumbs.length - 1 && navigate(crumb.path)}
          >
            {crumb.label}
          </Link>
        ))}
      </Breadcrumbs>
      {/* Page Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#030007',
            marginBottom: 1,
          }}
        >
          Organization Setup
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#4F4937',
            fontSize: '0.95rem',
            lineHeight: 1.6,
          }}
        >
          Manage your company information and organizational details.
        </Typography>
      </Box>

      {/* Tabs Section */}
      <Box
        sx={{
          marginBottom: 4,
          display: 'inline-flex',
          gap: 0.5,
          backgroundColor: '#F5EEFC',
          borderRadius: '16px',
          p: 0.75,
          border: '1px solid rgba(117, 34, 209, 0.08)',
          boxShadow: '0 4px 12px rgba(117, 34, 209, 0.02)',
          overflowX: 'auto',
          maxWidth: '100%',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Button
            key={tab.value}
            onClick={() => handleTabChange(null, index)}
            sx={{
              textTransform: 'none',
              fontSize: '0.925rem',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              fontWeight: 700,
              paddingX: 3.5,
              paddingY: 1.2,
              borderRadius: '12px',
              color: activeTab === index ? '#FFFFFF' : '#5C378B',
              backgroundColor: activeTab === index ? '#7522D1' : 'transparent',
              boxShadow: activeTab === index ? '0 4px 14px rgba(117, 34, 209, 0.2)' : 'none',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: activeTab === index ? '#6A1DB0' : '#EADBF9',
                transform: activeTab === index ? 'none' : 'translateY(-1px)',
              },
            }}
          >
            {tab.label}
          </Button>
        ))}
      </Box>

      {/* Tab Content */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid rgba(117, 34, 209, 0.08)',
          boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
          mb: 4,
        }}
      >
        <Box sx={{ paddingTop: 1 }}>
          {activeTab === 0 && (
            <PersonalInformation
              formData={formData}
              handleInputChange={handleInputChange}
              handleFileUpload={handleFileUpload}
              handleRemoveLogo={handleRemoveLogo}
              fileInputRef={fileInputRef}
              errors={errors}
              touched={touched}
            />
          )}
          {activeTab === 1 && (
            <OperationalInformation
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              touched={touched}
            />
          )}
          {activeTab === 2 && (
            <LegalInfo
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              touched={touched}
            />
          )}
          {activeTab === 3 && (
            <SocialMediaLinks
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              touched={touched}
            />
          )}
          {activeTab === 4 && (
            <OnboardingTemplates />
          )}
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            paddingX: 4.5,
            paddingY: 1.5,
            borderRadius: '12px',
            border: '1px solid rgba(117, 34, 209, 0.2)',
            color: '#7522D1',
            backgroundColor: '#FFFFFF',
            fontWeight: 700,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: '#7522D1',
              backgroundColor: '#F5EEFC',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSubmitting || isUpdating || isCreating}
          sx={{
            textTransform: 'none',
            paddingX: 5.5,
            paddingY: 1.5,
            borderRadius: '12px',
            backgroundColor: '#7522D1',
            color: '#FFFFFF',
            fontWeight: 750,
            boxShadow: '0 4px 14px rgba(117, 34, 209, 0.2)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#6A1DB0',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 20px rgba(117, 34, 209, 0.3)',
            },
          }}
        >
          {isSubmitting || isUpdating || isCreating ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
        confirmText={alertDialog.confirmText}
        cancelText={alertDialog.cancelText}
        showCancel={alertDialog.showCancel}
        onConfirm={alertDialog.onConfirm}
      />
    </Container>
  );
};

export default OrgSetup;
